import type { TokenSchema } from './tokenSchema.ts';

/**
 * #GR3: which brand-kit tokens to load on mount. Prefer the in-progress DRAFT (the font
 * pick + edits autosaved to wizard_state) so a navigate-away/remount doesn't reset them —
 * but ONLY while brand_kit is unlocked. Once locked, the committed tokens are the truth,
 * so a stale draft must never override them → return null and let the caller load the
 * committed tokens. Pure, so the lock-gating rule is unit-tested.
 */
export function pickBrandDraft(draft: TokenSchema | undefined | null, brandLocked: boolean): TokenSchema | null {
  return draft && !brandLocked ? draft : null;
}

/**
 * #GR87: fold a fresh extraction into the editor's current tokens. A re-extract is a
 * WHOLE-brand AI redo: with NO artboards this run there's no colour reference, so the
 * extractor INVENTS a palette (its prompt derives "a tasteful default palette from the
 * fonts") — surfacing colours the designer never gave (the `stg-fix2-1` scare). When there's
 * no colour reference we carry the CURRENT palette forward (merge-not-clobber): take the
 * extraction's fonts/type/grid/button, KEEP the existing `color` block. With artboards
 * present the extraction's palette IS the reference, so it lands as-is. Pure so the rule is
 * unit-tested. Returns { tokens, paletteKept } — paletteKept drives the one-line UI note.
 */
export function mergeExtractedTokens(
  proposed: TokenSchema,
  current: TokenSchema,
  hasColourReference: boolean,
): { tokens: TokenSchema; paletteKept: boolean } {
  if (hasColourReference) return { tokens: proposed, paletteKept: false };
  return { tokens: { ...proposed, color: current.color }, paletteKept: true };
}
